/* eslint-disable react-refresh/only-export-components -- shadcn idiom: variants/helpers ship beside their component; HMR falls back to reload here. */
import * as React from 'react';
import { toast } from 'sonner';
import {
  CheckCircleIcon,
  InfoIcon,
  WarningCircleIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import { cn } from '@/components/ui/utils';

export type StatusToastTone = 'default' | 'success' | 'warn' | 'danger';

export interface StatusToastProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  /** Rendered tabular (`num`) — order numbers/amounts (spec §5). */
  body?: React.ReactNode;
  /** Colours the mark and decides whether the message may wrap. */
  tone?: StatusToastTone;
  action?: { label: React.ReactNode; onClick: () => void };
  onDismiss?: () => void;
  className?: string;
}

const TONE_CHIP: Record<StatusToastTone, string> = {
  default: 'bg-well text-muted',
  success: 'bg-pay-tint text-pay-tint-foreground',
  warn: 'bg-warn-tint text-warn-tint-foreground',
  danger: 'bg-danger-tint text-danger-tint-foreground',
};

const TONE_ICON: Record<StatusToastTone, React.ReactNode> = {
  default: <InfoIcon size={18} weight="fill" />,
  success: <CheckCircleIcon size={18} weight="fill" />,
  warn: <WarningIcon size={18} weight="fill" />,
  danger: <WarningCircleIcon size={18} weight="fill" />,
};

/**
 * A card, not a slab: the same surface as everything else on screen, with the
 * mark carrying the outcome. A failure states what went wrong in full — the
 * message used to be truncated to one line, which is where server errors go
 * to die.
 */
export function StatusToast({
  icon,
  title,
  body,
  tone = 'default',
  action,
  onDismiss,
  className,
}: StatusToastProps) {
  const failed = tone === 'danger' || tone === 'warn';
  return (
    <div
      data-slot="status-toast"
      data-tone={tone}
      role={failed ? 'alert' : 'status'}
      className={cn(
        'flex w-[356px] max-w-[calc(100vw-2rem)] animate-up items-start gap-3 rounded-card border border-border bg-elevated p-3.5 shadow-overlay',
        className,
      )}
    >
      {/*
        The tone chip carries the outcome, and a FAILURE is allowed to wrap.

        The older shape had a bare icon and truncated the body to one line,
        which is where a server's explanation of what went wrong goes to die —
        the operator saw "Could not save the ord…" and had nothing to act on.
      */}
      <span
        className={cn(
          'grid size-8 shrink-0 place-items-center rounded-full [&_svg]:shrink-0',
          TONE_CHIP[tone],
        )}
      >
        {icon ?? TONE_ICON[tone]}
      </span>

      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-body font-strong leading-tight text-text">{title}</p>
        {body ? (
          <p
            className={cn(
              'num mt-1 text-label font-mid leading-snug text-muted',
              failed ? '[overflow-wrap:anywhere]' : 'truncate',
            )}
          >
            {body}
          </p>
        ) : null}
      </div>

      {action ? (
        <button
          type="button"
          onClick={action.onClick}
          className="h-8 shrink-0 rounded-full bg-well px-3 text-label font-strong text-text transition-colors hover:bg-border-2"
        >
          {action.label}
        </button>
      ) : null}

      {onDismiss ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="-mr-1 -mt-1 grid size-7 shrink-0 place-items-center rounded-full text-faint transition-colors hover:bg-well hover:text-text"
        >
          <XIcon size={14} weight="bold" />
        </button>
      ) : null}
    </div>
  );
}

/** Fires a StatusToast through sonner (Toaster must be mounted). */
export function showStatusToast(props: StatusToastProps & { duration?: number }): string | number {
  const { duration, ...rest } = props;
  // A failure stays until it is read or dismissed; a confirmation goes away.
  const failed = rest.tone === 'danger' || rest.tone === 'warn';
  return toast.custom((id) => <StatusToast {...rest} onDismiss={() => toast.dismiss(id)} />, {
    duration: duration ?? (failed ? 10000 : 4000),
    unstyled: true,
  });
}
